import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavActions } from '../components/NavBar/NavActions';
import { MobileUnsupported } from '../components/MobileUnsupported';
import { Button } from '../components/SubscriptionAppButton';
import { Input } from '../components/SubscriptionAppInput';
import { Card } from '../components/SubscriptionAppCard';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Zap, Filter, ChevronRight, Trash2, Info, Repeat, Loader2, AlertCircle, AlertTriangle, Clock, ShieldAlert, Users, X, History, Gauge } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTags } from '../contexts/TagsContext';
import { ACTION_DEFINITIONS, ACTION_GROUPS, ALLOCATION_OPTIONS, ASSIGNEE_OPTIONS, AutomationCategory, CATEGORIES, CONDITION_FIELDS, DAY_OPTIONS, DEFAULT_SAFETY, DEFAULT_SCORE_BANDS, FREQUENCY_OPTIONS, getActionDefinition, getTriggerEvent, INITIAL_RULES, isChangeAction, MONTH_OPTIONS, PLAN_OPTIONS, PREVIEW_MEMBERS, PRODUCT_OPTIONS, RULE_KLAVIYO_METRIC, RuleAction, RuleCondition, RuleConditionGroup, RuleSafety, RuleType, RunMode, ScoreBand, TRIGGER_EVENT_GROUPS, TRIGGER_EVENTS, TriggerType } from '../data/mockAutomationRules';

// --- Operators by field type ---
const OPERATORS: Record<string, string[]> = {
  numeric: ['equals', 'does not equal', 'is greater than', 'is less than', 'is greater than or equal to', 'is less than or equal to', 'is between'],
  text: ['equals', 'does not equal', 'contains', 'does not contain'],
  date: ['is before', 'is after', 'is within last', 'is more than ago'],
  duration: ['is greater than', 'is less than', 'equals'],
  select: ['is', 'is not']
};
const SELECT_OPTIONS: Record<string, string[]> = {
  'Subscription Status': ['Active', 'Paused', 'Cancelled', 'Expired'],
  'Spend Trend': ['Up', 'Down', 'Stable'],
  'Payment Method Status': ['Valid', 'Expiring soon', 'Expired', 'Missing'],
  'Plan Name': PLAN_OPTIONS,
  'Last Product Swapped': PRODUCT_OPTIONS,
  'Most Ordered Product': PRODUCT_OPTIONS,
  'Day of Week': DAY_OPTIONS,
  'Month of Year': MONTH_OPTIONS
};
const DURATION_FIELDS = ['Member Tenure'];
const DATE_FIELDS = ['Signup Date', 'Subscription Anniversary', 'Card Expiry'];
const NUMERIC_FIELDS = ['Order Count', 'Box Edits Last 90d', 'Skips Last 90d', 'Consecutive Skips', 'Lifetime Spend', 'Average Order Value', 'Current Plan Price', 'Top Varietal % (Red)', 'Top Varietal % (White)', 'Varietals Tried Count', 'Failed Payments Last 90d', 'Days Since Last Order', 'Days Until Next Billing'];
const getFieldType = (field: string) => {
  if (field === 'Has Tag') return 'tag';
  if (SELECT_OPTIONS[field]) return 'select';
  if (DURATION_FIELDS.includes(field)) return 'duration';
  if (DATE_FIELDS.includes(field)) return 'date';
  if (NUMERIC_FIELDS.includes(field)) return 'numeric';
  return 'text';
};
const getOperators = (field: string) => {
  const t = getFieldType(field);
  if (t === 'tag') return ['is applied', 'is not applied'];
  return OPERATORS[t] || OPERATORS.text;
};
const generateId = () => Math.random().toString(36).substr(2, 9);

// --- Styling ---
const selectClasses = 'w-full h-[40px] bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200 cursor-pointer';
const conditionSelectClasses = 'h-[36px] bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200 cursor-pointer';
const conditionInputClasses = 'h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200';
const errorRing = 'border-[#EF4444] focus:border-[#EF4444] focus:ring-[#EF4444]/20';
const selectPadding: React.CSSProperties = {
  paddingLeft: 12,
  paddingRight: 40
};
const BAND_STYLES: Record<string, string> = {
  green: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]',
  amber: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]',
  orange: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]',
  red: 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]'
};
const emptyGroups = (): RuleConditionGroup[] => [{
  id: 'group-1',
  logic: 'AND',
  conditions: [{
    id: generateId(),
    field: 'Order Count',
    operator: 'is greater than',
    value: '',
    weight: 25
  }]
}];
export function CreateAutomationRulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    id
  } = useParams();
  const {
    allTags
  } = useTags();
  const existingRule = id ? INITIAL_RULES.find(r => r.id === id) : undefined;
  const isEdit = Boolean(existingRule);

  // A duplicate, a template or a saved view arrives as a payload on router state.
  const seed = (location.state as any)?.rule || (location.state as any)?.template || undefined;
  const savedView = (location.state as any)?.savedView || undefined;
  const source = existingRule || seed;
  const [ruleName, setRuleName] = useState(source?.name || savedView?.name || '');
  const [description, setDescription] = useState(source?.description || '');
  const [category, setCategory] = useState<AutomationCategory>(source?.category || 'Uncategorised');
  const [ruleType, setRuleType] = useState<RuleType>(source?.ruleType || 'match');
  const [triggerType, setTriggerType] = useState<TriggerType>(source?.triggerType || 'Scheduled (Daily)');
  const [event, setEvent] = useState(source?.event || 'Subscription Created');
  const [runMode, setRunMode] = useState<RunMode>(source?.runMode || 'once');
  const [safety, setSafety] = useState<RuleSafety>(source?.safety || DEFAULT_SAFETY);
  const [conditionGroups, setConditionGroups] = useState<RuleConditionGroup[]>(source?.conditionGroups?.length ? source.conditionGroups : savedView?.conditions?.length ? [{
    id: 'group-1',
    logic: 'AND',
    conditions: savedView.conditions
  }] : emptyGroups());
  const [metActions, setMetActions] = useState<RuleAction[]>(source?.metActions?.length ? source.metActions : [{
    id: generateId(),
    type: 'Add Tag',
    value: ''
  }]);
  const [exitActions, setExitActions] = useState<RuleAction[]>(source?.exitActions || []);
  const [scoreBands, setScoreBands] = useState<ScoreBand[]>(source?.scoreBands?.length ? source.scoreBands : DEFAULT_SCORE_BANDS);
  const [isPaused, setIsPaused] = useState(existingRule?.status === 'paused');
  const [dirty, setDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [savedBanner, setSavedBanner] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isCounting, setIsCounting] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(existingRule?.membersMatched ?? null);
  const countTimer = useRef<any>(null);
  const markDirty = () => setDirty(true);
  const activeTrigger = getTriggerEvent(event);
  const isScored = ruleType === 'scored';

  // --- Validation ---
  const conditionIncomplete = (c: RuleCondition) => {
    if (getFieldType(c.field) === 'tag') return !c.value.trim();
    if (!c.value.trim()) return true;
    if (c.operator === 'is between' && !(c.value2 || '').trim()) return true;
    return false;
  };
  const actionIncomplete = (a: RuleAction) => {
    if (a.type === 'Send Klaviyo Event') return false;
    if (a.type === 'Pause Subscription' || a.type === 'Resume Subscription') return false;
    if (!a.value.trim()) return true;
    if (a.type === 'Create Task' && !(a.secondaryValue || '').trim()) return true;
    return false;
  };
  const allConditions = conditionGroups.flatMap(g => g.conditions);
  const conditionsComplete = allConditions.length > 0 && allConditions.every(c => !conditionIncomplete(c));
  const bandActions = scoreBands.flatMap(b => b.actions);
  const metComplete = isScored ? bandActions.length > 0 && bandActions.every(a => !actionIncomplete(a)) : metActions.length > 0 && metActions.every(a => !actionIncomplete(a));
  const exitComplete = exitActions.every(a => !actionIncomplete(a));
  const nameComplete = ruleName.trim().length > 0;
  const weightTotal = allConditions.reduce((acc, c) => acc + (Number(c.weight) || 0), 0);
  const errors: string[] = [];
  if (!nameComplete) errors.push('Give the rule a name.');
  if (!conditionsComplete) errors.push('Every condition needs a complete value.');
  if (isScored && weightTotal !== 100) errors.push(`Condition weights must total 100. They currently total ${weightTotal}.`);
  if (isScored && bandActions.length === 0) errors.push('At least one score band needs an action.');
  if (!isScored && metActions.length === 0) errors.push('Add at least one action for when conditions are met.');else if (!metComplete) errors.push('Finish the actions on this rule.');
  if (!exitComplete) errors.push('Finish the exit actions, or remove the incomplete ones.');
  const canActivate = errors.length === 0;

  // Any action that alters a member's subscription changes what activating this
  // rule means. It is called out separately, not buried in the action list.
  const changeActions = [...metActions, ...bandActions].filter(a => isChangeAction(a.type));
  const hasChangeActions = changeActions.length > 0;
  useEffect(() => {
    if (countTimer.current) clearTimeout(countTimer.current);
    if (!conditionsComplete) {
      setIsCounting(false);
      setMatchCount(null);
      return;
    }
    setIsCounting(true);
    countTimer.current = setTimeout(() => {
      const seedNum = allConditions.reduce((acc, c) => acc + c.field.length + c.value.length, 0);
      setMatchCount(40 + seedNum * 7 % 260);
      setIsCounting(false);
    }, 600);
    return () => countTimer.current && clearTimeout(countTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(conditionGroups)]);
  const leave = (to: string) => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate(to);
  };
  const handleSave = (mode: 'draft' | 'test' | 'activate') => {
    if (mode !== 'draft' && !canActivate) {
      setShowErrors(true);
      return;
    }
    setDirty(false);
    setShowErrors(false);
    if (mode === 'draft') {
      setSavedBanner('Draft saved.');
      return;
    }
    if (mode === 'test') {
      setSavedBanner('Test run started. The rule will evaluate every day and record what it would have done, without actioning anyone. Check the run history before going live.');
      return;
    }
    setSavedBanner(isEdit ? 'Changes saved. They will take effect on the next daily run.' : 'Rule activated. It will run for the first time on the next daily run.');
  };

  // --- Condition handlers ---
  const addConditionGroup = () => {
    markDirty();
    setConditionGroups([...conditionGroups, {
      id: generateId(),
      logic: 'AND',
      conditions: [{
        id: generateId(),
        field: 'Order Count',
        operator: 'is greater than',
        value: '',
        weight: 0
      }]
    }]);
  };
  const addCondition = (groupId: string) => {
    markDirty();
    setConditionGroups(conditionGroups.map(g => g.id === groupId ? {
      ...g,
      conditions: [...g.conditions, {
        id: generateId(),
        field: 'Order Count',
        operator: 'is greater than',
        value: '',
        weight: 0
      }]
    } : g));
  };
  const updateCondition = (groupId: string, conditionId: string, field: keyof RuleCondition, value: string) => {
    markDirty();
    setConditionGroups(conditionGroups.map(g => {
      if (g.id !== groupId) return g;
      return {
        ...g,
        conditions: g.conditions.map(c => {
          if (c.id !== conditionId) return c;
          const next: RuleCondition = {
            ...c,
            [field]: field === 'weight' ? Number(value) : value
          } as RuleCondition;
          if (field === 'field') {
            next.operator = getOperators(value)[0];
            next.value = '';
            next.value2 = '';
            next.unit = getFieldType(value) === 'duration' ? 'months' : 'days';
          }
          if (field === 'operator' && value !== 'is between') next.value2 = '';
          return next;
        })
      };
    }));
  };
  const deleteCondition = (groupId: string, conditionId: string) => {
    markDirty();
    setConditionGroups(conditionGroups.map(g => {
      if (g.id !== groupId) return g;
      const remaining = g.conditions.filter(c => c.id !== conditionId);
      return remaining.length === 0 ? null : {
        ...g,
        conditions: remaining
      };
    }).filter(Boolean) as RuleConditionGroup[]);
  };
  const setGroupLogic = (groupId: string, logic: 'AND' | 'OR') => {
    markDirty();
    setConditionGroups(conditionGroups.map(g => g.id === groupId ? {
      ...g,
      logic
    } : g));
  };

  // --- Action handlers (shared between match actions and band actions) ---
  const newAction = (type = 'Add Tag'): RuleAction => ({
    id: generateId(),
    type,
    value: type === 'Send Klaviyo Event' ? RULE_KLAVIYO_METRIC : ''
  });
  const applyTypeChange = (a: RuleAction, type: string): RuleAction => ({
    ...a,
    type,
    value: type === 'Send Klaviyo Event' ? RULE_KLAVIYO_METRIC : '',
    secondaryValue: ''
  });
  const mutateList = (list: RuleAction[], setter: (v: RuleAction[]) => void) => ({
    add: (type?: string) => {
      markDirty();
      setter([...list, newAction(type)]);
    },
    update: (actionId: string, field: keyof RuleAction, value: string) => {
      markDirty();
      setter(list.map(a => a.id !== actionId ? a : field === 'type' ? applyTypeChange(a, value) : {
        ...a,
        [field]: value
      }));
    },
    remove: (actionId: string) => {
      markDirty();
      setter(list.filter(a => a.id !== actionId));
    }
  });
  const updateBandActions = (bandId: string, next: RuleAction[]) => {
    markDirty();
    setScoreBands(scoreBands.map(b => b.id === bandId ? {
      ...b,
      actions: next
    } : b));
  };
  const updateBand = (bandId: string, field: keyof ScoreBand, value: any) => {
    markDirty();
    setScoreBands(scoreBands.map(b => b.id === bandId ? {
      ...b,
      [field]: value
    } : b));
  };
  const totalConditions = allConditions.length;

  // --- Value input ---
  const renderConditionValueInput = (condition: RuleCondition, groupId: string) => {
    const {
      field,
      operator,
      value,
      value2,
      unit
    } = condition;
    const type = getFieldType(field);
    const invalid = showErrors && conditionIncomplete(condition);
    const err = invalid ? ` ${errorRing}` : '';
    if (type === 'tag') {
      return <select className={`w-44 ${conditionSelectClasses}${err}`} style={selectPadding} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-8615" data-mp="1jhs3hnfkyzb2">
          <option value="" data-id="element-8616" data-mp="38zgnrzslv4bf">Select tag…</option>
          {allTags.map(t => <option key={t.id} value={t.label} data-id="element-8617" data-mp="341negxw09u6x">
              {t.label}
            </option>)}
        </select>;
    }
    if (type === 'select') {
      return <select className={`w-44 ${conditionSelectClasses}${err}`} style={selectPadding} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-8618" data-mp="3hsahh28su68t">
          <option value="" data-id="element-8619" data-mp="2q2fcxycde9zk">Select…</option>
          {SELECT_OPTIONS[field].map(o => <option key={o} value={o} data-id="element-8620" data-mp="2l32v5s0cvt3u">
              {o}
            </option>)}
        </select>;
    }
    if (type === 'duration') {
      return <div className="flex gap-2" data-id="element-8621" data-mp="2z3vo48b0eg4u">
          <input type="number" className={`w-20 ${conditionInputClasses}${err}`} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-8622" data-mp="2u37oiy8psqz0" />
          <select className={`w-28 ${conditionSelectClasses}`} style={selectPadding} value={unit || 'months'} onChange={e => updateCondition(groupId, condition.id, 'unit', e.target.value)} data-id="element-8623" data-mp="ee6a1xi4qyd7">
            <option value="days" data-id="element-8624" data-mp="334p2mvcoqcjh">Days</option>
            <option value="weeks" data-id="element-8625" data-mp="383js2g55qzck">Weeks</option>
            <option value="months" data-id="element-8626" data-mp="3828e5ni00b7q">Months</option>
            <option value="years" data-id="element-8627" data-mp="3c552luu7z1jl">Years</option>
          </select>
        </div>;
    }
    if (operator === 'is within last' || operator === 'is more than ago') {
      return <div className="flex gap-2" data-id="element-8628" data-mp="3h2dmbzv0ndmy">
          <input type="number" className={`w-20 ${conditionInputClasses}${err}`} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-8629" data-mp="2kf9zmvdfvdcg" />
          <select className={`w-24 ${conditionSelectClasses}`} style={selectPadding} value={unit || 'days'} onChange={e => updateCondition(groupId, condition.id, 'unit', e.target.value)} data-id="element-8630" data-mp="2tf5dx92ajogp">
            <option value="days" data-id="element-8631" data-mp="2y7q502ttbngj">Days</option>
            <option value="weeks" data-id="element-8632" data-mp="20pggf5osi6ju">Weeks</option>
            <option value="months" data-id="element-8633" data-mp="20ondygwv637i">Months</option>
          </select>
        </div>;
    }
    if (operator === 'is before' || operator === 'is after') {
      return <input type="date" className={`w-44 ${conditionInputClasses}${err}`} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-8634" data-mp="1y4qszcuvnjn5" />;
    }
    if (operator === 'is between') {
      return <div className="flex items-center gap-2" data-id="element-8635" data-mp="1o62olgvtf31x">
          <input type="number" className={`w-24 ${conditionInputClasses}${err}`} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-8636" data-mp="1k6tweggfbd8h" />
          <span className="text-xs text-[#64748B]" data-id="element-8637" data-mp="1aghc8pcitm2v">and</span>
          <input type="number" className={`w-24 ${conditionInputClasses}${err}`} value={value2 || ''} onChange={e => updateCondition(groupId, condition.id, 'value2', e.target.value)} data-id="element-8638" data-mp="1aewdenj6bgz7" />
        </div>;
    }
    return <input type={type === 'numeric' ? 'number' : 'text'} className={`w-44 ${conditionInputClasses}${err}`} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-8639" data-mp="10k4ffilois3w" />;
  };
  const renderActionInput = (action: RuleAction, ops: ReturnType<typeof mutateList>) => {
    const {
      type,
      value,
      secondaryValue
    } = action;
    const invalid = showErrors && actionIncomplete(action);
    const err = invalid ? ` ${errorRing}` : '';
    if (type === 'Add Tag' || type === 'Remove Tag') {
      return <select className={`w-56 ${conditionSelectClasses}${err}`} style={selectPadding} value={value} onChange={e => ops.update(action.id, 'value', e.target.value)} data-id="element-8640" data-mp="2aeosd92par2w">
          <option value="" data-id="element-8641" data-mp="1wm08y6egx5m1">Select tag…</option>
          {allTags.map(t => <option key={t.id} value={t.label} data-id="element-8642" data-mp="21g4imxf4brqt">
              {t.label}
            </option>)}
        </select>;
    }
    if (type === 'Send Klaviyo Event') {
      return <div className="flex items-center gap-2 flex-1" data-id="element-8643" data-mp="1ro769ncditqi">
          <code className="bg-[#F1F5F9] border border-[#E2E8F0] px-2.5 py-1.5 rounded-[6px] text-[13px] text-[#0F172A]" data-id="element-8644" data-mp="1rmnplvmt2go7">
            {RULE_KLAVIYO_METRIC}
          </code>
          <span className="text-xs text-[#94A3B8]" data-id="element-8645" data-mp="1imqw9gph0tmt">
            with this rule's name and category attached
          </span>
        </div>;
    }
    if (type === 'Create Task') {
      return <div className="flex gap-2 flex-1" data-id="element-8646" data-mp="u479u7jypz7u">
          <input type="text" placeholder="Task title" className={`flex-1 ${conditionInputClasses}${err}`} value={value} onChange={e => ops.update(action.id, 'value', e.target.value)} data-id="element-8647" data-mp="u59vo12fhbgw" />
          <select className={`w-36 ${conditionSelectClasses}${err}`} style={selectPadding} value={secondaryValue || ''} onChange={e => ops.update(action.id, 'secondaryValue', e.target.value)} data-id="element-8648" data-mp="1401xjh31mkhh">
            <option value="" data-id="element-8649" data-mp="1d1i4tfyka6hh">Assignee…</option>
            {ASSIGNEE_OPTIONS.map(a => <option key={a} value={a} data-id="element-8650" data-mp="1rpw97vbmm305">
                {a}
              </option>)}
          </select>
        </div>;
    }
    if (type === 'Add Note to Contact' || type === 'Add to Action Required' || type === 'Flag as Moment to Reach Out') {
      return <input type="text" placeholder={type === 'Add Note to Contact' ? 'Note content…' : 'Reason shown to the team…'} className={`flex-1 ${conditionInputClasses}${err}`} value={value} onChange={e => ops.update(action.id, 'value', e.target.value)} data-id="element-8651" data-mp="20p9ionzr2rkv" />;
    }
    if (type === 'Change Frequency') {
      return <select className={`w-56 ${conditionSelectClasses}${err}`} style={selectPadding} value={value} onChange={e => ops.update(action.id, 'value', e.target.value)} data-id="element-8652" data-mp="24c0al59bbco3">
          <option value="" data-id="element-8653" data-mp="2davhyko3essz">Select frequency…</option>
          {FREQUENCY_OPTIONS.map(f => <option key={f} value={f} data-id="element-8654" data-mp="1plt31kkufe26">
              {f}
            </option>)}
        </select>;
    }
    if (type === 'Replace Product') {
      return <div className="flex gap-2 flex-1 items-center" data-id="element-8655" data-mp="1kjtyi7ddab3n">
          <select className={`flex-1 ${conditionSelectClasses}${err}`} style={selectPadding} value={value} onChange={e => ops.update(action.id, 'value', e.target.value)} data-id="element-8656" data-mp="1zbco8ww13k07">
            <option value="" data-id="element-8657" data-mp="16tqhpvx9b9ll">Wine to replace…</option>
            {PRODUCT_OPTIONS.map(p => <option key={p} value={p} data-id="element-8658" data-mp="11v6uls6xfrrn">
                {p}
              </option>)}
          </select>
          <span className="text-xs text-[#64748B]" data-id="element-8659" data-mp="3diioysjrzlt2">with</span>
          <select className={`flex-1 ${conditionSelectClasses}`} style={selectPadding} value={secondaryValue || ''} onChange={e => ops.update(action.id, 'secondaryValue', e.target.value)} data-id="element-8660" data-mp="3dh7ob1lmmu66">
            <option value="" data-id="element-8661" data-mp="34jf7ft09bego">Replacement…</option>
            {PRODUCT_OPTIONS.map(p => <option key={p} value={p} data-id="element-8662" data-mp="38inygq3wwy8j">
                {p}
              </option>)}
          </select>
        </div>;
    }
    if (type === 'Add to Allocation') {
      return <select className={`w-64 ${conditionSelectClasses}${err}`} style={selectPadding} value={value} onChange={e => ops.update(action.id, 'value', e.target.value)} data-id="element-8663" data-mp="52qmcbm6xg7k">
          <option value="" data-id="element-8664" data-mp="3n82e2fy30bwd">Select allocation…</option>
          {ALLOCATION_OPTIONS.map(a => <option key={a} value={a} data-id="element-8665" data-mp="3s5vgcxw9klrd">
              {a}
            </option>)}
        </select>;
    }
    if (type === 'Skip Next Order') {
      return <span className="text-sm text-[#64748B]" data-id="element-8666" data-mp="2gs9ubllpb0a2">
          Skips the next scheduled order
        </span>;
    }
    if (type === 'Pause Subscription' || type === 'Resume Subscription') {
      return <span className="text-sm text-[#64748B]" data-id="element-8667" data-mp="mdw180z447av">
          {type === 'Pause Subscription' ? 'Pauses the subscription' : 'Resumes the subscription'}
        </span>;
    }
    return null;
  };
  const renderActionRow = (action: RuleAction, ops: ReturnType<typeof mutateList>) => {
    const def = getActionDefinition(action.type);
    return <div key={action.id} className="flex items-center gap-3" data-id="element-8668" data-mp="3lto2zloj95d">
        <select className={`w-56 ${conditionSelectClasses} ${def?.kind === 'change' ? 'border-[#FDBA74]' : ''}`} style={selectPadding} value={action.type} onChange={e => ops.update(action.id, 'type', e.target.value)} data-id="element-8669" data-mp="3urusipb534i1">
          {ACTION_GROUPS.map(group => <optgroup key={group} label={group} data-id="element-8670" data-mp="3ls7ywponj2zj">
              {ACTION_DEFINITIONS.filter(a => a.group === group).map(a => <option key={a.type} value={a.type} data-id="element-8671" data-mp="1o0vbju52xmer">
                  {a.type}
                </option>)}
            </optgroup>)}
        </select>
        {renderActionInput(action, ops)}
        <IconButton icon={<Trash2 className="w-4 h-4" data-id="element-8673" data-mp="31wwzizpb90dz" />} variant="ghost" label="Delete action" className="text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2] flex-shrink-0" onClick={() => ops.remove(action.id)} data-id="element-8672" data-mp="31wdirmbeci1j" />
      </div>;
  };
  const metOps = mutateList(metActions, setMetActions);
  const exitOps = mutateList(exitActions, setExitActions);
  const renderMatchPanel = () => {
    if (!conditionsComplete) {
      return <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 mt-4 flex items-start gap-3" data-id="element-8674" data-mp="2j0ezlv0b5u6n">
          <Info className="w-5 h-5 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-8675" data-mp="2nxx85kaey7e4" />
          <p className="text-sm text-[#64748B]" data-id="element-8676" data-mp="2ryh80vmdfp3y">
            Finish the conditions above to see how many members match.
          </p>
        </div>;
    }
    if (isCounting) {
      return <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 mt-4 flex items-start gap-3" data-id="element-8677" data-mp="79t3ulyrunsg">
          <Loader2 className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5 animate-spin" data-id="element-8678" data-mp="c7346wnp8jv2" />
          <p className="text-sm text-[#64748B]" data-id="element-8679" data-mp="aq5t2k23aibg">Checking…</p>
        </div>;
    }
    return <div className="bg-[#E0F2FE] border border-[#BAE6FD] rounded-lg p-4 mt-4 flex items-start gap-3" data-id="element-8680" data-mp="5s4by4o84t2m">
        <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-8681" data-mp="js5avekxhqfg" />
        <div className="flex-1" data-id="element-8682" data-mp="jpasdslh6foo">
          <p className="text-sm text-[#0369A1]" data-id="element-8683" data-mp="fpsg1l7pzcxg">
            {triggerType === 'Scheduled (Daily)' ? <>
                <strong data-id="element-8684" data-mp="toru5yxlw9fd">{matchCount} members</strong> match and will be actioned
                on the next daily run.
              </> : <>
                <strong data-id="element-8685" data-mp="oom8cze0ph57">{matchCount} members</strong> currently match. Each will
                be actioned when they next trigger {event}.
              </>}
          </p>
          {/* Nobody switches on a rule touching 156 people without seeing who. */}
          <button onClick={() => setShowPreview(true)} className="text-sm text-[#0EA5E9] font-medium hover:underline mt-1.5 inline-flex items-center gap-1.5" data-id="element-8686" data-mp="1cfj7tcommlzk">
            <Users className="w-3.5 h-3.5" data-id="element-8687" data-mp="17fdhbzu3f5j2" />
            See who matches
          </button>
        </div>
      </div>;
  };
  return <>
      <div className="block md:hidden" data-id="element-8688" data-mp="1w2cshhuqei2i">
        <MobileUnsupported data-id="element-8689" data-mp="1w1bcr02rt7eg" />
      </div>

      <div className="hidden md:block bg-[#F1F5F9] min-h-full" data-id="element-8690" data-mp="1i2levi84n3p1">
        <nav className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-3 sticky top-0 z-10" data-id="element-8691" data-mp="1i4eck80ridkw">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-8692" data-mp="18cig11ebqvis">
            {isEdit ? 'Edit Automation Rule' : 'Create Automation Rule'}
          </h1>
          <div aria-hidden="true" className="flex-1" data-id="element-8693" data-mp="1d4tf6218qdaa"></div>

          {isEdit && <div className="flex items-center gap-2 mr-2" data-id="element-8694" data-mp="yf3zz2wbc8r0">
              <span className="text-xs text-[#64748B]" data-id="element-8695" data-mp="ychst4dc3v9b">
                {isPaused ? 'Paused' : 'Active'}
              </span>
              <button role="switch" aria-checked={!isPaused} aria-label={isPaused ? 'Resume rule' : 'Pause rule'} onClick={() => setIsPaused(!isPaused)} className={`w-11 h-6 rounded-full relative transition-colors ${!isPaused ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8696" data-mp="pd4hs79kaymh">
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${!isPaused ? 'left-6' : 'left-1'}`} data-id="element-8697" data-mp="2w3zm27vni486" />
              </button>
            </div>}

          <Button variant="ghost" onClick={() => leave('/settings/automation-rules')} data-id="element-8698" data-mp="2mcmulhdrlg2o">
            Cancel
          </Button>
          {!isEdit && <Button variant="ghost" onClick={() => handleSave('draft')} data-id="element-8699" data-mp="3f0sgw8kgvd4m">
              Save as Draft
            </Button>}
          {/* Test run is the safe way in: evaluates daily, records what it would
              have done, actions nobody. */}
          <Button variant="secondary" onClick={() => handleSave('test')} data-id="element-8700" data-mp="35yrl7g4vsz52">
            Start test run
          </Button>
          <Button variant="primary" onClick={() => handleSave('activate')} data-id="element-8701" data-mp="310q5n56nzhyc">
            {isEdit ? 'Save changes' : 'Activate Rule'}
          </Button>
          <NavActions data-id="element-8702" data-mp="1plb8a0ajmo2p" />
        </nav>

        <div className="p-6 max-w-5xl mx-auto" data-id="element-8703" data-mp="1pk9mp1vwij6q">
          <div className="flex items-center gap-2 text-sm mb-6" data-id="element-8704" data-mp="5422wjxg7ymf">
            <span className="text-[#64748B]" data-id="element-8705" data-mp="51pblg5swbwp">Settings</span>
            <ChevronRight className="w-4 h-4 text-[#94A3B8]" data-id="element-8706" data-mp="a3y5ceqhzhzv" />
            <button onClick={() => leave('/settings/automation-rules')} className="text-[#0EA5E9] hover:underline transition-colors duration-200" data-id="element-8707" data-mp="e4i05bnlm9iu">
              Automation Rules
            </button>
            <ChevronRight className="w-4 h-4 text-[#94A3B8]" data-id="element-8708" data-mp="nu9xgdn31gr0" />
            <span className="text-[#0F172A] font-medium" data-id="element-8709" data-mp="nu1noum9ark1">
              {isEdit ? existingRule?.name : 'Create Rule'}
            </span>
          </div>

          {savedView && <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg p-4 mb-6 flex items-start gap-3" data-id="element-8710" data-mp="xtir15e1ctcu">
              <Filter className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-8711" data-mp="11srksa92sr8e" />
              <p className="text-sm text-[#0369A1]" data-id="element-8712" data-mp="2zkm37z3kqfzi">
                Conditions have been carried across from the saved view{' '}
                <strong data-id="element-8713" data-mp="33lxu07y6p79a">{savedView.name}</strong>. Adjust them here — the saved
                view itself is not changed.
              </p>
            </div>}

          {savedBanner && <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-4 mb-6 flex items-start gap-3" data-id="element-8714" data-mp="2fqvsji3v923w">
              <Clock className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" data-id="element-8715" data-mp="2tg7foo9xwfyb" />
              <p className="text-sm text-[#065F46]" data-id="element-8716" data-mp="2pfnijkpd9cc6">{savedBanner}</p>
            </div>}

          {showErrors && errors.length > 0 && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 mb-6 flex items-start gap-3" data-id="element-8717" data-mp="21r4c7apvn9mq">
              <AlertCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" data-id="element-8718" data-mp="1wst1kxrrcv3g" />
              <div data-id="element-8719" data-mp="2arsq85f9t9zm">
                <p className="text-sm font-medium text-[#991B1B] mb-1" data-id="element-8720" data-mp="2asaw7trqj2kq">
                  This rule can't run yet
                </p>
                <ul className="list-disc pl-5 space-y-0.5" data-id="element-8721" data-mp="3m66c54qgrfd0">
                  {errors.map(e => <li key={e} className="text-sm text-[#991B1B]" data-id="element-8722" data-mp="3m896g0ynfunj">
                      {e}
                    </li>)}
                </ul>
              </div>
            </div>}

          {isEdit && <p className="text-xs text-[#94A3B8] mb-4" data-id="element-8723" data-mp="3pmsnugfmaywy">
              Created {existingRule?.createdAt} · Last modified{' '}
              {existingRule?.updatedAt}
            </p>}

          <div className="mb-6 space-y-4" data-id="element-8724" data-mp="3fr7okq1xocqw">
            <div data-id="element-8725" data-mp="3kozitqq1way9">
              <Input label="Rule Name" placeholder="e.g., Tag loyal members after 12 months" value={ruleName} onChange={e => {
              markDirty();
              setRuleName(e.target.value);
            }} data-id="element-8726" data-mp="3kohc205riec3" />
              {showErrors && !nameComplete && <p className="text-xs text-[#EF4444] mt-1" data-id="element-8727" data-mp="h7qz11brj10w">
                  Give the rule a name.
                </p>}
            </div>
            <Input label="Description" placeholder="Optional description for your team" value={description} onChange={e => {
            markDirty();
            setDescription(e.target.value);
          }} data-id="element-8728" data-mp="7ih9nt8azwgh" />
            <div data-id="element-8729" data-mp="zyi9y7beezhe">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-8730" data-mp="zz1ode1ub5qo">
                Category
              </label>
              <select className={selectClasses} style={selectPadding} value={category} onChange={e => {
              markDirty();
              setCategory(e.target.value as AutomationCategory);
            }} data-id="element-8731" data-mp="21yivq7km2zj7">
                {CATEGORIES.map(c => <option key={c} value={c} data-id="element-8732" data-mp="2gnp34itlrw26">
                    {c}
                  </option>)}
              </select>
              <p className="text-xs text-[#94A3B8] mt-1.5" data-id="element-8733" data-mp="1j0gbe630dqrq">
                Used to group and filter rules on the rules list.
              </p>
            </div>
          </div>

          {/* Card 0: Rule type. Scored is what lets a winery build its own
              risk score rather than only getting ours. */}
          <Card padding="none" className="mb-6" data-id="element-8734" data-mp="1wzy3ovfijm5u">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8735" data-mp="1wxlhu76mwrni">
              <Gauge className="w-5 h-5 text-[#6366F1]" data-id="element-8736" data-mp="38euw8fawothy" />
              <div data-id="element-8737" data-mp="38da38u48pim7">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8738" data-mp="38gxufmm7psxr">
                  Rule type
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-8739" data-mp="sq67c10f0iox">
                  How does this rule decide?
                </span>
              </div>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3" data-id="element-8740" data-mp="nx3a9iwfir3z">
              {[{
              key: 'match' as RuleType,
              title: 'Match',
              body: 'A member either meets the conditions or they don’t. One set of actions.'
            }, {
              key: 'scored' as RuleType,
              title: 'Scored',
              body: 'Each condition carries a weight. The total maps to bands, and each band has its own response.'
            }].map(opt => <label key={opt.key} className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-all ${ruleType === opt.key ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-8741" data-mp="20hx0yf25ir">
                  <input type="radio" name="ruleType" checked={ruleType === opt.key} onChange={() => {
                markDirty();
                setRuleType(opt.key);
              }} className="mt-1 accent-[#0EA5E9]" data-id="element-8742" data-mp="1uft8k78sgbdz" />
                  <div data-id="element-8743" data-mp="2icg12cq7phch">
                    <div className="text-sm font-medium text-[#0F172A]" data-id="element-8744" data-mp="24jgi20r81506">
                      {opt.title}
                    </div>
                    <div className="text-xs text-[#64748B] mt-0.5" data-id="element-8745" data-mp="29j2yej2cj1d7">{opt.body}</div>
                  </div>
                </label>)}
            </div>
          </Card>

          {/* Card 1: Trigger */}
          <Card padding="none" className="mb-6" data-id="element-8746" data-mp="31863u25yyhrf">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8747" data-mp="2n9okshr0dtfu">
              <Zap className="w-5 h-5 text-[#F59E0B]" data-id="element-8748" data-mp="2s89leia6af4t" />
              <div data-id="element-8749" data-mp="2s6y89ruu7v11">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8750" data-mp="2salxwfx5vxah">
                  Trigger
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-8751" data-mp="1gu5g4fy4x4kg">
                  When should this rule be evaluated?
                </span>
              </div>
            </div>
            <div className="p-5" data-id="element-8752" data-mp="19bxrxk0cz8qu">
              <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-8753" data-mp="14btb1svjgc03">
                <div data-id="element-8754" data-mp="14dcrbjh72n1v">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-8755" data-mp="1ibb6gr3canif">
                    Trigger Type
                  </label>
                  <select className={selectClasses} style={selectPadding} value={triggerType} onChange={e => {
                  markDirty();
                  setTriggerType(e.target.value as TriggerType);
                }} data-id="element-8756" data-mp="1dchreu8emog5">
                    <option value="Event-based" data-id="element-8757" data-mp="lipikau24572">Event-based</option>
                    <option value="Scheduled (Daily)" data-id="element-8758" data-mp="zdi5vvg6upo7">Scheduled (Daily)</option>
                  </select>
                </div>
                {triggerType === 'Event-based' && <div data-id="element-8759" data-mp="6uumckgubip8">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-8760" data-mp="6ubddt9644nc">
                      Event
                    </label>
                    <select className={selectClasses} style={selectPadding} value={event} onChange={e => {
                  markDirty();
                  setEvent(e.target.value);
                }} data-id="element-8761" data-mp="fja2dzpqqvvr">
                      {TRIGGER_EVENT_GROUPS.map(group => <optgroup key={group} label={group} data-id="element-8762" data-mp="pgenrxbrinbi">
                          {TRIGGER_EVENTS.filter(e2 => e2.group === group).map(e2 => <option key={e2.name} value={e2.name} data-id="element-8763" data-mp="u9pm1a2idvrt">
                                {e2.name}
                              </option>)}
                        </optgroup>)}
                    </select>
                  </div>}
              </div>
              <p className="text-xs text-[#94A3B8]" data-id="element-8764" data-mp="18879d6oi5697">
                {triggerType === 'Event-based' ? `This rule will check conditions each time ${event} fires.` : 'This rule runs once per day and evaluates all active subscribers.'}
              </p>

              {triggerType === 'Event-based' && activeTrigger?.warning && <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 mt-4 flex items-start gap-2.5" data-id="element-8765" data-mp="3exlsa291gn9v">
                  <AlertTriangle className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-8766" data-mp="1w4aqyfep0o36" />
                  <p className="text-xs text-[#92400E]" data-id="element-8767" data-mp="1rbhq42mchzwd">{activeTrigger.warning}</p>
                </div>}

              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 mt-4 flex items-start gap-2.5" data-id="element-8768" data-mp="1nc0ib2jg4qo1">
                <Clock className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" data-id="element-8769" data-mp="1idfgwzsgk0y0" />
                <p className="text-xs text-[#64748B]" data-id="element-8770" data-mp="2f13paq2pwf4d">
                  Whatever the trigger, exit criteria are evaluated once a day for
                  every rule. A member who stops matching is actioned on the next
                  daily run.
                </p>
              </div>
            </div>
          </Card>

          {/* Card 2: Conditions */}
          <Card padding="none" className="mb-6" data-id="element-8771" data-mp="uma46thtdorx">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8772" data-mp="un2zmybborur">
              <Filter className="w-5 h-5 text-[#8B5CF6]" data-id="element-8773" data-mp="plvrgy59pqnk" />
              <div className="flex-1" data-id="element-8774" data-mp="r4475cs5d3zc">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8775" data-mp="r55ilid2rshl">
                  Conditions
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-8776" data-mp="w12y5mgikutb">
                  {isScored ? 'Each condition contributes its weight to the score.' : 'Only apply to members who match these criteria.'}
                </span>
              </div>
              {isScored && <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${weightTotal === 100 ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#FEE2E2] text-[#991B1B]'}`} data-id="element-8777" data-mp="8d30e2ksc458">
                  Weights total {weightTotal} / 100
                </span>}
            </div>
            <div className="p-5" data-id="element-8778" data-mp="3ei7ypbryorc">
              {conditionGroups.length > 1 && <p className="text-xs text-[#64748B] mb-4" data-id="element-8779" data-mp="3cp23c8zk8ke">
                  Groups are joined with OR — a member matches the rule if they
                  match any one group.
                </p>}

              {conditionGroups.map((group, index) => <div key={group.id} data-id="element-8780" data-mp="36x601ltbscmb" data-mpi={index}>
                  {index > 0 && <div className="flex items-center gap-3 my-4" data-id="element-8781" data-mp="36xxweyuqyedn">
                      <div className="h-px bg-[#E2E8F0] flex-1" data-id="element-8782" data-mp="31zo02kxad7qg"></div>
                      <span className="bg-[#F1F5F9] text-[#475569] text-xs px-3 py-1 rounded-full font-medium" data-id="element-8783" data-mp="3gvlv1zvh4613">
                        OR
                      </span>
                      <div className="h-px bg-[#E2E8F0] flex-1" data-id="element-8784" data-mp="3byvfrerhtegq"></div>
                    </div>}

                  <div className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-8785" data-mp="3pugzb6eyj61r">
                    <div className="flex items-center justify-between mb-3" data-id="element-8786" data-mp="3kwp8ygtynrzo">
                      <span className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-8787" data-mp="3h8phbr952ng">
                        Group {index + 1}
                      </span>
                      {!isScored && <div className="flex bg-[#F1F5F9] rounded-[6px] p-0.5" data-id="element-8788" data-mp="cgvs20vpwei3">
                          <button aria-pressed={group.logic === 'AND'} onClick={() => setGroupLogic(group.id, 'AND')} className={`px-2.5 py-1 text-xs font-medium rounded-[4px] transition-all duration-200 ${group.logic === 'AND' ? 'bg-[#0EA5E9] text-white shadow-sm' : 'text-[#475569] hover:bg-[#E2E8F0]'}`} data-id="element-8789" data-mp="8gbu4v3bl6uw">
                            AND
                          </button>
                          <button aria-pressed={group.logic === 'OR'} onClick={() => setGroupLogic(group.id, 'OR')} className={`px-2.5 py-1 text-xs font-medium rounded-[4px] transition-all duration-200 ${group.logic === 'OR' ? 'bg-[#0EA5E9] text-white shadow-sm' : 'text-[#475569] hover:bg-[#E2E8F0]'}`} data-id="element-8790" data-mp="1or39lc28imbh">
                            OR
                          </button>
                        </div>}
                    </div>

                    <div className="space-y-3" data-id="element-8791" data-mp="19von6n7t5027">
                      {group.conditions.map(condition => {
                    const isLast = totalConditions <= 1;
                    return <div key={condition.id} className="flex items-center gap-3" data-id="element-8792" data-mp="dby11rff9tjy">
                            <select className={`flex-[3] min-w-0 ${conditionSelectClasses}`} style={selectPadding} value={condition.field} onChange={e => updateCondition(group.id, condition.id, 'field', e.target.value)} data-id="element-8793" data-mp="hdt3iz93aczb">
                              {Object.entries(CONDITION_FIELDS).map(([cat, fields]) => <optgroup key={cat} label={cat} data-id="element-8794" data-mp="u3pht83t13t">
                                    {fields.map(f => <option key={f} value={f} data-id="element-8795" data-mp="3s0od12yy9qw3">
                                        {f}
                                      </option>)}
                                  </optgroup>)}
                            </select>

                            <select className={`flex-[3] min-w-0 ${conditionSelectClasses}`} style={selectPadding} value={condition.operator} onChange={e => updateCondition(group.id, condition.id, 'operator', e.target.value)} data-id="element-8796" data-mp="3ialmraj7qr1g">
                              {getOperators(condition.field).map(op => <option key={op} value={op} data-id="element-8797" data-mp="34b3swgr28nl8">
                                  {op}
                                </option>)}
                            </select>

                            {renderConditionValueInput(condition, group.id)}

                            {isScored && <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-8798" data-mp="22ps3qiqc0zpq">
                                <input type="number" min={0} max={100} className={`w-16 ${conditionInputClasses}`} value={condition.weight ?? 0} onChange={e => updateCondition(group.id, condition.id, 'weight', e.target.value)} data-id="element-8799" data-mp="1xqf8papb4mc2" />
                                <span className="text-xs text-[#94A3B8]" data-id="element-8800" data-mp="2qfcq7blp2f3r">pts</span>
                              </div>}

                            <IconButton icon={<Trash2 className="w-4 h-4" data-id="element-8802" data-mp="2lgrm313bnba1" />} variant="ghost" label="Delete condition" disabled={isLast} title={isLast ? 'A rule needs at least one condition' : 'Delete condition'} className={`flex-shrink-0 ${isLast ? 'text-[#CBD5E1] cursor-not-allowed' : 'text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2]'}`} onClick={() => !isLast && deleteCondition(group.id, condition.id)} data-id="element-8801" data-mp="2lhb5yncj89qp" />
                          </div>;
                  })}
                    </div>

                    <button onClick={() => addCondition(group.id)} className="text-[#0EA5E9] text-xs font-medium mt-3 hover:underline transition-colors duration-200" data-id="element-8803" data-mp="3brbll99qbipx">
                      + Add condition
                    </button>
                  </div>
                </div>)}

              {!isScored && <button onClick={addConditionGroup} className="text-[#0EA5E9] text-sm font-medium mt-4 hover:underline transition-colors duration-200" data-id="element-8804" data-mp="3qfyaa1l8xmtt">
                  + Add condition group
                </button>}

              {renderMatchPanel()}
            </div>
          </Card>

          {/* Card 3: Actions — bands when scored, met/exit when match */}
          {isScored ? <Card padding="none" className="mb-6" data-id="element-8805" data-mp="d84rhnndh6d0">
              <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8806" data-mp="d7li4vbsfqal">
                <Gauge className="w-5 h-5 text-[#6366F1]" data-id="element-8807" data-mp="2a2zq38ed37um" />
                <div data-id="element-8808" data-mp="2f0j2s3vevckg">
                  <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8809" data-mp="2ezzw5jb7oalq">
                    Score bands
                  </span>
                  <span className="text-xs text-[#64748B] ml-2" data-id="element-8810" data-mp="22xgj3nmxvj08">
                    What happens at each score range?
                  </span>
                </div>
              </div>
              <div className="p-5 space-y-4" data-id="element-8811" data-mp="2guvauz0o02vd">
                {scoreBands.map(band => {
              const bandOps = mutateList(band.actions, next => updateBandActions(band.id, next));
              return <div key={band.id} className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-8812" data-mp="2pvtf9if0mceh">
                      <div className="flex items-center gap-3 mb-3" data-id="element-8813" data-mp="2lw2kl3mdbc4o">
                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${BAND_STYLES[band.colour]}`} data-id="element-8814" data-mp="2zmebiz77v4u8">
                          {band.label}
                        </span>
                        <div className="flex items-center gap-2" data-id="element-8815" data-mp="39myeq7eaqjf3">
                          <input type="number" className={`w-16 ${conditionInputClasses}`} value={band.min} onChange={e => updateBand(band.id, 'min', Number(e.target.value))} data-id="element-8816" data-mp="34n2duh0wb83r" />
                          <span className="text-xs text-[#64748B]" data-id="element-8817" data-mp="3o6rimrk6ak07">to</span>
                          <input type="number" className={`w-16 ${conditionInputClasses}`} value={band.max} onChange={e => updateBand(band.id, 'max', Number(e.target.value))} data-id="element-8818" data-mp="3s9edz2g1yn25" />
                          <span className="text-xs text-[#94A3B8]" data-id="element-8819" data-mp="3j8g3q2f0tjb4">points</span>
                        </div>
                      </div>
                      {band.actions.length === 0 ? <p className="text-xs text-[#94A3B8] mb-2" data-id="element-8820" data-mp="39b36mg8m4slr">
                          Nothing happens in this band. Members are scored but
                          not actioned.
                        </p> : <div className="space-y-3" data-id="element-8821" data-mp="30j19orxr5ug8">
                          {band.actions.map(a => renderActionRow(a, bandOps))}
                        </div>}
                      <button onClick={() => bandOps.add()} className="text-[#0EA5E9] text-xs font-medium mt-3 hover:underline" data-id="element-8822" data-mp="2lmiuywqg0scd">
                        + Add action
                      </button>
                    </div>;
            })}
              </div>
            </Card> : <Card padding="none" className="mb-6" data-id="element-8823" data-mp="3owehxpq8cb8g">
              <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8824" data-mp="3oyr8gra1ztx4">
                <Zap className="w-5 h-5 text-[#10B981]" data-id="element-8825" data-mp="3f6uyoiylufd2" />
                <div data-id="element-8826" data-mp="3f6brnxahtbdt">
                  <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8827" data-mp="3f4qv624mbidb">
                    Actions
                  </span>
                  <span className="text-xs text-[#64748B] ml-2" data-id="element-8828" data-mp="366ex5f1f9iau">
                    What should happen when conditions are met?
                  </span>
                </div>
              </div>
              <div className="p-5" data-id="element-8829" data-mp="2w88wt1v9kk83">
                <div className="border-l-4 border-[#10B981] pl-4 mb-6" data-id="element-8830" data-mp="2w9alwa94778t">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-3" data-id="element-8831" data-mp="zg8qtvxfz9uz">
                    When conditions are MET
                  </h3>
                  {metActions.length === 0 ? <div className="border border-dashed border-[#CBD5E1] rounded-lg p-4 text-sm text-[#64748B]" data-id="element-8832" data-mp="uet8wcqk9jgt">
                      No actions yet. A rule needs at least one action here
                      before it can run.
                    </div> : <div className="space-y-3" data-id="element-8833" data-mp="1bpoofnwjsr1r">
                      {metActions.map(a => renderActionRow(a, metOps))}
                    </div>}
                  <button onClick={() => metOps.add()} className="text-[#0EA5E9] text-xs font-medium mt-3 hover:underline transition-colors duration-200" data-id="element-8834" data-mp="1gr48pdqwitj3">
                    + Add action
                  </button>

                  {metActions.some(a => a.type === 'Send Klaviyo Event') && <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 mt-3 flex items-start gap-2.5" data-id="element-8835" data-mp="synsz9umzdmy">
                      <Info className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" data-id="element-8836" data-mp="xzuwgwpqxqnt" />
                      <p className="text-xs text-[#64748B]" data-id="element-8837" data-mp="24e3ydw0toj68">
                        Every rule sends the same Klaviyo metric,{' '}
                        <span className="text-[#0F172A]" data-id="element-8838" data-mp="9bh36m9d64y7">
                          {RULE_KLAVIYO_METRIC}
                        </span>
                        , carrying the rule name and category. In Klaviyo, build
                        one flow on that metric and filter it to this rule by
                        name.
                      </p>
                    </div>}
                </div>

                <div className="border-l-4 border-[#EF4444] pl-4" data-id="element-8839" data-mp="e8pk6jis02xv">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-3" data-id="element-8840" data-mp="3mqvgp5zb1epc">
                    When conditions are NO LONGER MET (exit criteria)
                  </h3>
                  {exitActions.length === 0 ? <div className="border border-dashed border-[#CBD5E1] rounded-lg p-4 text-sm text-[#64748B]" data-id="element-8841" data-mp="3vk8jr0r31wp5">
                      Nothing happens when a member stops matching. Anything this
                      rule applied stays on the member.
                    </div> : <div className="space-y-3" data-id="element-8842" data-mp="2z1b0guujcvjr">
                      {exitActions.map(a => renderActionRow(a, exitOps))}
                    </div>}
                  <button onClick={() => exitOps.add()} className="text-[#0EA5E9] text-xs font-medium mt-3 hover:underline transition-colors duration-200" data-id="element-8843" data-mp="2ovjie639eeqy">
                    + Add action
                  </button>
                </div>
              </div>
            </Card>}

          {/* Card 4: Safety. Only shown when it matters. */}
          <Card padding="none" className="mb-6" data-id="element-8844" data-mp="37nlqgt1u4mpl">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8845" data-mp="37nviem6fx3xx">
              <ShieldAlert className="w-5 h-5 text-[#EF4444]" data-id="element-8846" data-mp="3ckcgo97csyoq" />
              <div data-id="element-8847" data-mp="2xo4nwkhvqoxu">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8848" data-mp="2xomtw8ucghiy">
                  Safety
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-8849" data-mp="32qc0ncaja918">
                  Limits on what this rule can do in one run.
                </span>
              </div>
            </div>
            <div className="p-5 space-y-4" data-id="element-8850" data-mp="1radqt67j7iyy">
              {hasChangeActions && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 flex items-start gap-3" data-id="element-8851" data-mp="18jkybra20y9e">
                  <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" data-id="element-8852" data-mp="13iowrkd6do75" />
                  <div data-id="element-8853" data-mp="ylewf9o8zs4j">
                    <p className="text-sm font-medium text-[#991B1B] mb-1" data-id="element-8854" data-mp="ykndozfr1ovo">
                      This rule changes members' subscriptions
                    </p>
                    <ul className="list-disc pl-5 space-y-0.5" data-id="element-8855" data-mp="1r9atifhqbm1u">
                      {changeActions.map(a => <li key={a.id} className="text-sm text-[#991B1B]" data-id="element-8856" data-mp="1m9g2mbxyewgs">
                          {a.type}
                          {a.value ? ` — ${a.value}` : ''}
                        </li>)}
                    </ul>
                    <p className="text-xs text-[#991B1B] mt-2" data-id="element-8857" data-mp="1dgl7wc644j59">
                      Run this as a test first. Every change is written to the
                      Activity Log against the member.
                    </p>
                  </div>
                </div>}

              <div data-id="element-8858" data-mp="3nvtveji4vr2d">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-8859" data-mp="3nwc1e7ulljnh">
                  Maximum members actioned per run
                </label>
                <div className="flex items-center gap-3" data-id="element-8860" data-mp="qug2hsvmrdy">
                  <input type="number" className={`w-32 ${conditionInputClasses}`} value={safety.maxPerRun ?? ''} placeholder="No limit" onChange={e => {
                  markDirty();
                  setSafety({
                    ...safety,
                    maxPerRun: e.target.value ? Number(e.target.value) : null
                  });
                }} data-id="element-8861" data-mp="paz0ozeccb4" />
                  <span className="text-xs text-[#64748B]" data-id="element-8862" data-mp="kebpwipu2w54">
                    If more members match than this, the rule stops at the limit
                    and reports rather than actioning everyone.
                  </span>
                </div>
              </div>

              {hasChangeActions && <label className="flex items-start gap-3 cursor-pointer" data-id="element-8863" data-mp="ye37sujh0woe">
                  <input type="checkbox" checked={safety.requireApprovalForChanges} onChange={e => {
                markDirty();
                setSafety({
                  ...safety,
                  requireApprovalForChanges: e.target.checked
                });
              }} className="mt-0.5 accent-[#0EA5E9]" data-id="element-8864" data-mp="yc9xn59nfubn" />
                  <div data-id="element-8865" data-mp="1unngp38eqf43">
                    <div className="text-sm text-[#0F172A]" data-id="element-8866" data-mp="1up71n750dec7">
                      Hold subscription changes for approval
                    </div>
                    <div className="text-xs text-[#64748B] mt-0.5" data-id="element-8867" data-mp="2ig41vmnfyn7m">
                      Matched members go to the Action Required queue for someone
                      to confirm before anything changes.
                    </div>
                  </div>
                </label>}
            </div>
          </Card>

          {/* Card 5: Repeat behaviour */}
          <Card padding="none" className="mb-6" data-id="element-8868" data-mp="31bu51d741pn3">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8869" data-mp="363c60194vq82">
              <Repeat className="w-5 h-5 text-[#0EA5E9]" data-id="element-8870" data-mp="3fhma4azjxpyk" />
              <div data-id="element-8871" data-mp="3fg1gqooz4d2a">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8872" data-mp="3bgiunplf1vxv">
                  Repeat behaviour
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-8873" data-mp="3p877jv3hoofw">
                  What happens if a member matches again later?
                </span>
              </div>
            </div>
            <div className="p-5 space-y-3" data-id="element-8874" data-mp="2wiqgp1vipfls">
              {[{
              key: 'once' as RunMode,
              title: 'Run once per member',
              body: 'A member is actioned the first time they match. If they exit and match again, nothing happens a second time.'
            }, {
              key: 'every' as RunMode,
              title: 'Run every time conditions are met',
              body: 'A member is actioned each time they match, including after previously exiting the rule.'
            }].map(opt => <label key={opt.key} className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-all ${runMode === opt.key ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-8875" data-mp="gsx86uqzhxbt">
                  <input type="radio" name="runMode" checked={runMode === opt.key} onChange={() => {
                markDirty();
                setRunMode(opt.key);
              }} className="mt-1 accent-[#0EA5E9]" data-id="element-8876" data-mp="ssu7g91rvd6n" />
                  <div data-id="element-8877" data-mp="12qibxqp6jz20">
                    <div className="text-sm font-medium text-[#0F172A]" data-id="element-8878" data-mp="5547v018rzzc">
                      {opt.title}
                    </div>
                    <div className="text-xs text-[#64748B] mt-0.5" data-id="element-8879" data-mp="a4yyr3l0opke">{opt.body}</div>
                  </div>
                </label>)}
            </div>
          </Card>

          {/* Card 6: Run history, edit mode only */}
          {isEdit && existingRule && existingRule.runs.length > 0 && <Card padding="none" className="mb-6" data-id="element-8880" data-mp="3ik8za799dmq2">
              <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-8881" data-mp="3ijh2wu7u7kyq">
                <History className="w-5 h-5 text-[#64748B]" data-id="element-8882" data-mp="3ngh8l40rylpb" />
                <div data-id="element-8883" data-mp="3fysskx2ujzwj">
                  <span className="text-sm font-semibold text-[#0F172A]" data-id="element-8884" data-mp="3fx88k1e5rzcx">
                    Run history
                  </span>
                  <span className="text-xs text-[#64748B] ml-2" data-id="element-8885" data-mp="360v6af4jsd6k">
                    What this rule has actually done.
                  </span>
                </div>
              </div>
              <div className="p-5" data-id="element-8886" data-mp="2x0ghv6waehyu">
                <div className="space-y-2" data-id="element-8887" data-mp="2x0ozfc3cubgd">
                  {existingRule.runs.map(run => <div key={run.id} className="flex items-center gap-4 text-sm py-2 border-b border-[#F1F5F9] last:border-0" data-id="element-8888" data-mp="2sa039jgqcqaw">
                      <span className="text-[#0F172A] w-52" data-id="element-8889" data-mp="2nbotf8qfqfso">{run.date}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${run.mode === 'test' ? 'bg-[#EDE9FE] text-[#5B21B6]' : 'bg-[#D1FAE5] text-[#065F46]'}`} data-id="element-8890" data-mp="2ic2hr3q997lz">
                        {run.mode === 'test' ? 'Test' : 'Live'}
                      </span>
                      <span className="text-[#64748B]" data-id="element-8891" data-mp="29d8e4teibgyx">
                        {run.matched} matched
                      </span>
                      <span className="text-[#64748B]" data-id="element-8892" data-mp="3314pi91143o3">
                        {run.actioned} actioned
                      </span>
                      {run.heldByCap > 0 && <span className="text-[#B45309]" data-id="element-8893" data-mp="2p740w83sjx1g">
                          {run.heldByCap} held by the cap
                        </span>}
                    </div>)}
                </div>
                <button onClick={() => navigate(`/operations/activity-log?ruleId=${existingRule.id}`)} className="text-[#0EA5E9] text-sm font-medium mt-4 hover:underline" data-id="element-8894" data-mp="2f9pja564t27k">
                  View in Activity Log
                </button>
              </div>
            </Card>}
        </div>
      </div>

      {/* Member preview drawer */}
      {showPreview && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-8895" data-mp="4ig088fehdup">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setShowPreview(false)} data-id="element-8896" data-mp="4gv268tvncs3" />
          <div className="relative bg-white w-full max-w-2xl h-full shadow-xl flex flex-col animate-in slide-in-from-right duration-200" data-id="element-8897" data-mp="1t1ty2k77itw1">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-8898" data-mp="1p2uvv7cv1b8a">
              <div className="flex-1" data-id="element-8899" data-mp="22tzldgmikn4w">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-8900" data-mp="1y1ookmn2lhgk">
                  Members who match
                </h2>
                <p className="text-sm text-[#64748B]" data-id="element-8901" data-mp="2cpt79qm4hszc">
                  {matchCount} members · showing the first{' '}
                  {PREVIEW_MEMBERS.length}
                </p>
              </div>
              <IconButton icon={<X className="w-4 h-4" data-id="element-8903" data-mp="2lpzjza9zyoxg" />} variant="ghost" label="Close" onClick={() => setShowPreview(false)} data-id="element-8902" data-mp="2lrj090vnkzz8" />
            </div>
            <div className="flex-1 overflow-auto" data-id="element-8904" data-mp="2gq3d939x0je7">
              <table className="w-full text-sm" data-id="element-8905" data-mp="rcgfthlgo836">
                <thead className="bg-[#F8FAFC] sticky top-0" data-id="element-8906" data-mp="rfcdr5xb6wrm">
                  <tr className="text-left text-[12px] font-semibold text-[#475569] uppercase tracking-[0.5px]" data-id="element-8907" data-mp="mfgj3xai4hoq">
                    <th className="px-6 py-3" data-id="element-8908" data-mp="2mhza4j0i7h18">Member</th>
                    <th className="px-6 py-3" data-id="element-8909" data-mp="2rezhcx93aptx">Plan</th>
                    <th className="px-6 py-3" data-id="element-8910" data-mp="2rhlmkq8ict8z">Tenure</th>
                    <th className="px-6 py-3" data-id="element-8911" data-mp="2wecau0uguv5h">Value</th>
                    {isScored && <th className="px-6 py-3" data-id="element-8912" data-mp="30g7al0wr10h5">Score</th>}
                  </tr>
                </thead>
                <tbody data-id="element-8913" data-mp="35fjyzq39qflu">
                  {PREVIEW_MEMBERS.map(m => <tr key={m.id} className="border-b border-[#F1F5F9]" data-id="element-8914" data-mp="35er0tdidkyfb">
                      <td className="px-6 py-3" data-id="element-8915" data-mp="3adll6jvzqv1j">
                        <div className="text-[#0F172A] font-medium" data-id="element-8916" data-mp="3ac0mqj6k2rye">{m.name}</div>
                        <div className="text-xs text-[#94A3B8]" data-id="element-8917" data-mp="3f5vem9wbed8k">{m.email}</div>
                      </td>
                      <td className="px-6 py-3 text-[#64748B]" data-id="element-8918" data-mp="3k3dihlvh629p">{m.plan}</td>
                      <td className="px-6 py-3 text-[#64748B]" data-id="element-8919" data-mp="3o72yvo1zxdjn">{m.tenure}</td>
                      <td className="px-6 py-3 text-[#64748B]" data-id="element-8920" data-mp="3t439mc9sj4h3">
                        {m.lifetimeValue}
                      </td>
                      {isScored && <td className="px-6 py-3 text-[#0F172A] font-medium" data-id="element-8921" data-mp="1hfetb1zpwan7">
                          {m.score}
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center gap-3" data-id="element-8922" data-mp="13i00dna99kqh">
              <Button variant="secondary" data-id="element-8923" data-mp="207zjgbo181nb">Export list</Button>
              <div className="flex-1" data-id="element-8924" data-mp="207hg6v2ycn5w" />
              <Button variant="ghost" onClick={() => setShowPreview(false)} data-id="element-8925" data-mp="1w6dx7zplyb0g">
                Close
              </Button>
            </div>
          </div>
        </div>}
    </>;
}